import React, { useEffect, useRef, useState } from 'react'
import {
  CreateMssRequestDto,
  MssProviderEmailDto,
  MssRequestDto,
  UpdateMssRequestDto,
} from '@services/dtos'
import { yupResolver } from '@hookform/resolvers/yup'
import { FieldError, useForm } from 'react-hook-form'
import {
  Button,
  Dialog,
  DialogContent,
  FormControl,
  FormHelperText,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Step,
  StepLabel,
  Stepper,
  Tooltip,
  Typography,
} from '@mui/material'
import { DateInputField } from '../inputs/DateInputField'
import { Spinner } from '../loadings'
import moment from 'moment'
import {
  FileTypeEnumLabels,
  MssRequestPriorityLabels,
  MssRequestStatusLabels,
} from '@utils/constants'
import ExamSelectorDialog from '../dialogs/ExamSelectorDialog'
import {
  Add,
  Close as CloseIcon,
  Visibility as VisibilityOnIcon,
} from '@mui/icons-material'
import { useAppDispatch } from '@hooks/reduxHooks'
import { findAll as findAllExam } from '@state/thunks/procedureThunk'
import { useSelector } from '@state/store'
import { enqueueSnackbar as notistackEnqueueSnackbar } from 'notistack'
import {
  createRequest,
  getRequests,
  updateRequest,
} from '@state/reducers/mssReducer'
import { enqueueSnackbar } from '@state/reducers/alertReducer'
import InputField from '../inputs/InputField'
import SelectField from '../inputs/SelectField'
import MssEmailForm from './MssEmailForm'
import { UpTransition } from '../animations'
import ServerAutocomplete from '../autocomplete/ServerAutocomplete'
import {
  FunctionalUnitDTO,
  FunctionalUnitService,
} from '@services/functionalUnitService'
import { DoctorDto } from '@/services/api'
import { DoctorV2Service } from '@services/DoctorService'
import { findAll as findAllSite } from '@state/thunks/siteThunk'
import EventIcon from '@mui/icons-material/Event'
import SettingsIcon from '@mui/icons-material/Settings'
import MedicalServicesOutlinedIcon from '@mui/icons-material/MedicalServicesOutlined'
import theme from '@utils/theme'
import AttachmentIcon from '@mui/icons-material/Attachment'
import {
  setPatientsFilter,
  setSuggestedFilters,
} from '@state/reducers/patientsReducer'
import {
  doCreatePatient,
  fetchSuggestPatients,
} from '@state/thunks/patientsThunk'
import { DataGridPro } from '@mui/x-data-grid-pro'
import PatientSearchComponent from '../patient/PatientSearchComponent'
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd'
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted'
import ManagePatientComponent, {
  ManagepatientComponentRef,
} from './ManagePatientComponent'
import { dummyPdfBase64 } from '@utils/mocks'
import DoctorForm from './DoctorForm'
import { createMssProcessedEmailStepSchemas } from '@utils/schemas'

type Props = {
  email?: MssProviderEmailDto // create
  doctorId?: number // create
  editingRequest?: MssRequestDto // update
  onClose: () => void
}

const MssRequestForm: React.FC<Props> = ({
  email,
  doctorId,
  editingRequest,
  onClose,
}) => {
  const [activeStep, setActiveStep] = useState(0)
  const [previewAttachment, setPreviewAttachment] = useState<number>(0)
  const [patientFormOpen, setPatientFormOpen] = useState(false)
  const [doctorFormOpen, setDoctorFormOpen] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isSubmittingPatient, setIsSubmittingPatient] = useState(false)
  const [isExamSelectorVisible, setIsExamSelectorVisible] = useState(false)
  const [emailOpen, isEmailOpen] = useState(false)
  const [patientIdErrorFirstClean, setPatientIdErrorFirstClean] =
    useState(false)
  const managePatientRef = useRef<ManagepatientComponentRef>(null)

  const {
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    reset,
    control,
    trigger,
    clearErrors,
  } = useForm<UpdateMssRequestDto>({
    resolver: yupResolver(
      createMssProcessedEmailStepSchemas((email?.attachments ?? []).length)[
        activeStep
      ],
    ),
    defaultValues: {
      attachments: email
        ? email.attachments.map(() => ({
            date: undefined,
            type: undefined,
          }))
        : [],
      examIds: [],
    },
  })

  const steps =
    (email?.attachments ?? []).length > 0
      ? ['Documents', 'Détails', 'Patient']
      : ['Détails', 'Patient']

  const dispatch = useAppDispatch()

  const {
    exams,
    sites,
    suggestedFilters,
    suggestedPatients,
    suggestedLoading,
    patientFilters,
  } = useSelector(({ procedure, site, patients }) => ({
    exams: procedure.exams.datas,
    sites: site.sites.datas,
    suggestedFilters: patients.suggestedFilters,
    suggestedPatients: patients.suggestedPatients,
    suggestedLoading: patients.suggestedLoading,
    patientFilters: patients.patientsFilter,
  }))

  useEffect(() => {
    dispatch(findAllExam({}))
    dispatch(findAllSite())
    dispatch(setSuggestedFilters({ page: 0, limit: 5, ssn: '291234567890123' })) // TODO: mock
    return () => {
      reset()
    }
  }, [])

  useEffect(() => {
    if (errors.patientId && !patientIdErrorFirstClean) {
      setPatientIdErrorFirstClean(true)
      clearErrors('patientId')
    }
  }, [errors])

  useEffect(() => {
    dispatch(fetchSuggestPatients())
  }, [suggestedFilters])

  const handleNext = async () => {
    const isValid = await trigger()
    if (isValid) setActiveStep((prevStep) => prevStep + 1)
  }

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1)
  }

  const removeExam = (removedExamId: number) => {
    const isConfirmed = window.confirm(
      'Voulez-vous vraiment supprimer cet examen ?',
    )
    if (!isConfirmed) {
      return
    }

    setValue(
      'examIds',
      (watch('examIds') || []).filter((examId) => examId !== removedExamId),
    )
  }

  const cb = async (msg: string) => {
    dispatch(
      enqueueSnackbar({
        message: msg,
        options: { variant: 'success' },
      }),
    )
    onClose()
  }

  const onSubmit = async (data: CreateMssRequestDto) => {
    console.log('onSubmit', data)
    /*setIsSubmitting(true)

    if (editingRequest) {
      dispatch(updateRequest({ id: editingRequest.id, dto: data }))
        .unwrap()
        .then(async () => cb('La demande a été modifiée avec succès'))
        .finally(() => {
          setIsSubmitting(false)
          dispatch(getRequests())
        })
    } else {
      if (!doctorId || !email) return

      dispatch(
        createRequest({ doctorId, messageId: email.messageId, dto: data }),
      )
        .unwrap()
        .then(async () => cb('La demande a été créée avec succès'))
        .finally(() => {
          setIsSubmitting(false)
          dispatch(getRequests())
        })
    }*/
  }

  const fetchFunctionalUnits = async (search: string) => {
    const response = await FunctionalUnitService.getAll({
      search,
    })
    return response.datas
  }

  const fetchReferringDoctor = async (search: string) => {
    const response = await DoctorV2Service.findMany({
      page: 1,
      limit: 20,
      search,
    })
    return response.datas
  }

  const patientColumns = [
    {
      headerName: 'Prénom',
      field: 'firstName',
      width: 160,
    },
    {
      headerName: 'Nom',
      field: 'lastName',
      width: 160,
    },
    {
      headerName: 'Date de naissance',
      field: 'birthDate',
      width: 160,
      valueFormatter: ({ value }) =>
        moment(value as string).format('DD/MM/YYYY'),
    },
    {
      headerName: 'INS',
      field: 'ins',
      width: 160,
    },
    {
      headerName: 'SSN',
      field: 'ssn',
      width: 160,
    },
  ]

  const patientId = watch('patientId')

  // TODO: mock
  const patientData = [
    { label: 'Nom', value: 'Non renseigné' },
    { label: 'Prénom', value: 'Non renseigné' },
    { label: 'Date de naissance', value: 'Non renseigné' },
    { label: 'SSN', value: '291234567890123' },
    { label: 'INS', value: 'Non renseigné' },
  ]

  const handleSavePatient = async () => {
    setIsSubmittingPatient(true)
    try {
      const dto = await managePatientRef.current
        ?.getPatientFormRef()
        ?.getValues()
      if (dto) {
        await dispatch(doCreatePatient(dto)).unwrap()
      }
    } catch (err) {
      notistackEnqueueSnackbar((err as Error).message, { variant: 'error' })
    } finally {
      setIsSubmittingPatient(false)
    }
  }

  return (
    <div className="w-full h-full p-4">
      <form onSubmit={handleSubmit(onSubmit)} className="w-full">
        <div className="flex flex-wrap justify-between mb-12">
          <div className="flex items-center space-x-4">
            <h2 className="text-2xl font-bold text-gray-800">
              Transformer en demande
            </h2>
            {email && (
              <Button
                variant="outlined"
                startIcon={<VisibilityOnIcon />}
                onClick={() => isEmailOpen(true)}
              >
                {'Voir l\'email'}
              </Button>
            )}
          </div>
          <Stepper activeStep={activeStep} alternativeLabel className="gap-3">
            {steps.map((label, index) => (
              <Step key={label}>
                <StepLabel
                  StepIconProps={{
                    style: {
                      color:
                        activeStep === index
                          ? '#3A0CA1'
                          : theme.palette.primary.main,
                    },
                  }}
                >
                  <span
                    style={{
                      color:
                        activeStep === index
                          ? '#3A0CA1'
                          : theme.palette.primary.main,
                    }}
                  >
                    {label}
                  </span>
                </StepLabel>
              </Step>
            ))}
          </Stepper>
          <div className="flex items-center space-x-4">
            <button
              type="button"
              className="px-4 py-2 bg-white border border-indigo-700 text-indigo-700 rounded-lg flex items-center hover:bg-gray-100 disabled:opacity-50"
              disabled={activeStep === 0}
              onClick={handleBack}
            >
              Précédent
            </button>
            {activeStep === steps.length - 1 ? (
              <button
                type="submit"
                className="px-4 py-2 bg-indigo-700 hover:bg-indigo-800 text-white rounded-lg flex items-center disabled:opacity-50"
                disabled={isSubmitting}
              >
                {isSubmitting && <Spinner className="mr-2" size={18} />}
                Enregistrer
              </button>
            ) : (
              <button
                type="button"
                className="px-4 py-2 bg-indigo-700 hover:bg-indigo-800 text-white rounded-lg flex items-center disabled:opacity-50"
                onClick={handleNext}
              >
                Suivant
              </button>
            )}
            <button
              type="button"
              className="px-4 py-2 bg-gray-400 hover:bg-gray-500 text-white rounded-lg flex items-center disabled:opacity-50"
              disabled={isSubmitting}
              onClick={onClose}
            >
              {isSubmitting && <Spinner className="mr-2" size={18} />}
              Fermer
            </button>
          </div>
        </div>

        <div className="flex flex-wrap">
          {activeStep === 0 && email && email.attachments.length > 0 && (
            <div className="w-full">
              <fieldset className="border border-gray-200 p-4 rounded-lg">
                <legend className="text-lg font-bold mb-2 border border-gray-200 p-2 rounded-lg">
                  <AttachmentIcon /> Pièces jointes
                </legend>
                <div className="flex flex-col md:flex-row">
                  <div className="w-full md:w-[350px] flex-shrink-0 pr-4 max-h-[400px] md:max-h-none md:h-[calc(100vh-300px)] overflow-y-auto mb-8 md:mb-0">
                    {email.attachments.map((attachment, index) => (
                      <div key={index} className="mb-4 p-4">
                        <div
                          className="flex cursor-pointer"
                          onClick={() => setPreviewAttachment(index)}
                        >
                          {previewAttachment === index ? (
                            <VisibilityOnIcon
                              fontSize="small"
                              sx={{ color: '#3A0CA1' }}
                              className="mr-2 cursor-pointer"
                            />
                          ) : (
                            <Tooltip title="Prévisualiser le document">
                              <VisibilityOnIcon
                                fontSize="small"
                                className="mr-2 cursor-pointer"
                              />
                            </Tooltip>
                          )}

                          <p
                            className="font-semibold mb-4"
                            style={{
                              color:
                                previewAttachment === index
                                  ? '#3A0CA1'
                                  : theme.palette.primary.main,
                            }}
                          >
                            {attachment.fileName}
                          </p>
                        </div>

                        <FormControl
                          fullWidth
                          error={!!errors.attachments?.[index]?.date?.message}
                        >
                          <DateInputField
                            label="Date"
                            name={`attachments.${index}.date`}
                            value={
                              watch(`attachments.${index}.date`)
                                ? moment(watch(`attachments.${index}.date`))
                                : null
                            }
                            sx={{
                              border: errors.attachments?.[index]?.date?.message
                                ? '1px solid #d3302f'
                                : 'none',
                              borderRadius: '13px',
                              '& .MuiInputLabel-root': {
                                color: errors.attachments?.[index]?.date
                                  ?.message
                                  ? '#d3302f'
                                  : '',
                              },
                            }}
                            onChange={(date) => {
                              setValue(
                                `attachments.${index}.date`,
                                date ? date.toDate() : new Date(),
                              )
                            }}
                          />

                          {!!errors.attachments?.[index]?.date?.message && (
                            <FormHelperText error>
                              {errors.attachments?.[index]?.date?.message}
                            </FormHelperText>
                          )}
                        </FormControl>

                        <FormControl
                          fullWidth
                          error={!!errors.attachments?.[index]?.type?.message}
                          className="!mt-4"
                        >
                          <SelectField
                            control={control}
                            name={`attachments.${index}.type`}
                            options={Object.entries(FileTypeEnumLabels).map(
                              ([value, label]) => ({
                                label,
                                value,
                              }),
                            )}
                            value={watch(`attachments.${index}.type`)}
                            label="Type"
                          />
                        </FormControl>
                      </div>
                    ))}
                  </div>

                  <div className="flex-1 flex items-center justify-center border-t md:border-l md:border-t-0 border-gray-200 pr-4 pl-4">
                    <iframe
                      src={`data:application/pdf;base64,${dummyPdfBase64}`}
                      title="Document Preview"
                      className="w-full h-[600px]"
                    />
                  </div>
                </div>
              </fieldset>
            </div>
          )}

          {activeStep === (steps.length === 2 ? 0 : 1) && (
            <>
              <div className="w-full lg:w-1/2 px-2">
                <fieldset className="border border-gray-200 p-4 rounded-lg">
                  <legend className="text-lg font-bold mb-2 border border-gray-200 p-2 rounded-lg">
                    <SettingsIcon /> Paramètres
                  </legend>
                  <div className="flex flex-col w-full">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:gap-2 w-full">
                      <div className="w-full sm:w-1/2">
                        <SelectField
                          control={control}
                          name="status"
                          options={Object.entries(MssRequestStatusLabels).map(
                            ([value, label]) => ({
                              label,
                              value,
                            }),
                          )}
                          value={watch('priority')}
                          label="Statut"
                        />
                      </div>

                      <div className="w-full sm:w-1/2 flex items-center">
                        <ServerAutocomplete<DoctorDto>
                          fetchService={fetchReferringDoctor}
                          onChange={(value: DoctorDto | null) => {
                            setValue('referringDoctorId', value?.id)
                          }}
                          getValue={(value) => value?.id?.toString() || null}
                          getOptionLabel={(value) => {
                            const doctor = value as DoctorDto
                            return doctor
                              ? `${doctor.firstName} ${doctor.lastName} (RPPS : ${doctor.rppsNumber})`
                              : ''
                          }}
                          debounceMs={300}
                          placeholder="Rechercher un médecin"
                          label="Médecin demandeur"
                          helperText={errors.referringDoctorId?.message}
                          error={!!errors.referringDoctorId}
                          minSearchLength={0}
                        />
                        <span className="inline-flex items-center">
                          <Tooltip title="Enregistrer un nouveau médecin" arrow>
                            <Button
                              variant="outlined"
                              color="primary"
                              onClick={() => setDoctorFormOpen(true)}
                              sx={{
                                height: 55,
                                paddingLeft: 1.5,
                                paddingRight: 1.5,
                                minWidth: 'auto',
                                width: 'auto',
                                borderColor: '#e5e7eb',
                              }}
                            >
                              <i className="fas fa-plus"></i>
                            </Button>
                          </Tooltip>
                        </span>
                      </div>
                    </div>

                    <div className="flex flex-col sm:flex-row sm:items-center sm:gap-2 w-full mt-2">
                      <div className="w-full sm:w-1/2">
                        <SelectField
                          control={control}
                          name="siteId"
                          options={sites.map(({ id, label }) => ({
                            label,
                            value: id,
                          }))}
                          value={watch('siteId')}
                          label="Site"
                        />
                      </div>

                      <div className="w-full sm:w-1/2 flex items-center">
                        <ServerAutocomplete<FunctionalUnitDTO>
                          fetchService={fetchFunctionalUnits}
                          onChange={(value: FunctionalUnitDTO | null) => {
                            setValue('functionalUnitId', value?.id)
                          }}
                          getValue={(value) => value?.id?.toString() || null}
                          getOptionLabel={(value) => {
                            const functionalUnit = value as FunctionalUnitDTO
                            return functionalUnit
                              ? `${functionalUnit.label} (${functionalUnit.name})`
                              : ''
                          }}
                          debounceMs={300}
                          placeholder="Rechercher une unité fonctionnelle"
                          label="UF demandeuse"
                          helperText={errors.functionalUnitId?.message}
                          error={!!errors.functionalUnitId}
                          minSearchLength={0}
                        />
                        <span className="inline-flex items-center">
                          <Tooltip title="Enregistrer une nouvelle UF" arrow>
                            <Button
                              variant="outlined"
                              color="primary"
                              onClick={() => {}}
                              sx={{
                                height: 55,
                                paddingLeft: 1.5,
                                paddingRight: 1.5,
                                minWidth: 'auto',
                                width: 'auto',
                                borderColor: '#e5e7eb',
                              }}
                            >
                              <i className="fas fa-plus"></i>
                            </Button>
                          </Tooltip>
                        </span>
                      </div>
                    </div>
                  </div>
                </fieldset>
              </div>

              <div className="w-full lg:w-1/2 px-2 mt-4 lg:mt-0">
                <fieldset className="border border-gray-200 p-4 rounded-lg min-h-[150px]">
                  <legend className="text-lg font-bold mb-2 border border-gray-200 p-2 rounded-lg">
                    <EventIcon /> Rendez-vous
                  </legend>

                  <div className="w-full sm:w-1/2">
                    <SelectField
                      control={control}
                      name="priority"
                      options={[
                        { label: 'Non spécifiée', value: '' },
                        ...Object.entries(MssRequestPriorityLabels).map(
                          ([value, label]) => ({
                            label,
                            value,
                          }),
                        ),
                      ]}
                      value={watch('priority')}
                      label="Priorité"
                    />
                  </div>

                  <div className="w-full mt-2">
                    <InputField
                      control={control}
                      name={'priorityComment'}
                      label={'Commentaire'}
                      type="string"
                    />
                  </div>
                </fieldset>
              </div>

              <div className="w-full xl:w-1/2 px-2 mt-4 lg:mt-6">
                <fieldset className="border border-gray-200 p-4 rounded-lg">
                  <legend className="text-lg font-bold mb-4 border border-gray-200 p-2 rounded-lg">
                    <MedicalServicesOutlinedIcon /> Examens
                  </legend>
                  <div className="w-full mb-4">
                    <InputField
                      control={control}
                      name={'indication'}
                      label={'Indication'}
                      type="text"
                      multiline
                      rows={2}
                    />
                  </div>
                  <Button
                    variant="contained"
                    onClick={() => setIsExamSelectorVisible(true)}
                    size="small"
                    sx={{
                      border: errors.examIds ? '1px solid #d3302f' : 'none',
                    }}
                  >
                    <Add />
                    Ajouter un exam
                  </Button>
                  {errors.examIds && (
                    <FormHelperText error>
                      {(errors.examIds as unknown as FieldError).message}
                    </FormHelperText>
                  )}

                  {(watch('examIds') ?? []).length > 0 && (
                    <List
                      className="border border-1"
                      style={{ padding: 0, marginTop: 16 }}
                    >
                      {exams
                        .filter(({ id }) =>
                          (watch('examIds') ?? []).includes(id),
                        )
                        .map((selectedExam) => (
                          <ListItem
                            key={selectedExam.id}
                            button
                            className={'hover:bg-gray-100'}
                            secondaryAction={
                              <IconButton
                                onClick={() => removeExam(selectedExam.id)}
                                edge="end"
                                aria-label="delete"
                              >
                                <CloseIcon />
                              </IconButton>
                            }
                          >
                            <ListItemText
                              style={{ fontSize: '10px' }}
                              primary={`(${selectedExam.code}) ${selectedExam.label}`}
                            />
                          </ListItem>
                        ))}
                    </List>
                  )}
                </fieldset>
              </div>
            </>
          )}

          {activeStep === (steps.length === 2 ? 1 : 2) && (
            <>
              <div className="w-full xl:w-1/3 px-2 mb-4">
                <fieldset className="border border-gray-200 p-4 rounded-lg">
                  <legend className="text-lg font-bold mb-2 border border-gray-200 p-2 rounded-lg">
                    <FormatListBulletedIcon /> Informations reçues
                  </legend>
                  <div className="space-y-2 pl-2">
                    {patientData.map((item, index) => (
                      <Typography key={index}>
                        <span className="font-medium text-gray-800">
                          {item.label} :
                        </span>{' '}
                        {item.value}
                      </Typography>
                    ))}
                  </div>
                </fieldset>
              </div>

              <div className="w-full xl:w-2/3 px-2">
                <fieldset className="border border-gray-200 p-4 rounded-lg">
                  <legend className="text-lg font-bold border border-gray-200 p-2 rounded-lg">
                    <AssignmentIndIcon /> Choix patient{' '}
                    <span className="inline-flex items-center">
                      <Tooltip title="Ajouter un nouveau patient" arrow>
                        <Button
                          variant="outlined"
                          color="primary"
                          onClick={() => setPatientFormOpen(true)}
                          sx={{
                            paddingTop: 0.5,
                            paddingBottom: 0.5,
                            paddingLeft: 1,
                            paddingRight: 1,
                            minWidth: 'auto',
                            width: 'auto',
                            marginLeft: 1,
                          }}
                        >
                          <i className="fas fa-plus"></i>
                        </Button>
                      </Tooltip>
                    </span>
                  </legend>
                  <div>
                    {errors.patientId && (
                      <Typography color="error">
                        {errors.patientId.message}
                      </Typography>
                    )}
                    <div className="flex mb-2 items-center">
                      <Typography variant="body1">
                        Sélectionnez parmi les suggestions ci-dessous ou :
                      </Typography>
                      <div className="w-[300px] ml-4">
                        <PatientSearchComponent
                          isPatientExists
                          disabled={!!patientId}
                          onChange={(patientId?: number) => {
                            setValue('patientId', patientId)
                          }}
                        />
                      </div>
                    </div>
                    {!patientFilters.search && (
                      <DataGridPro
                        density="standard"
                        autoHeight
                        style={{
                          width: '100%',
                          backgroundColor: 'white',
                          borderRadius: '15px 15px 25px 25px',
                        }}
                        columns={patientColumns}
                        rows={suggestedPatients.datas}
                        rowCount={suggestedPatients.totalCount}
                        disableSelectionOnClick
                        pagination
                        paginationMode="server"
                        pageSize={5}
                        onPageChange={(page) => {
                          dispatch(setPatientsFilter({ page }))
                        }}
                        loading={suggestedLoading}
                        checkboxSelection={true}
                        disableMultipleSelection
                        selectionModel={patientId ? [patientId] : []}
                        onSelectionModelChange={(newSelectionModel) => {
                          if (!suggestedPatients) return
                          setValue(
                            'patientId',
                            suggestedPatients.datas.find(
                              ({ id }) =>
                                id ===
                                +newSelectionModel[
                                  newSelectionModel.length > 1
                                    ? newSelectionModel.length - 1
                                    : 0
                                ],
                            )?.id,
                          )
                        }}
                      />
                    )}
                  </div>
                </fieldset>
              </div>
            </>
          )}
        </div>
      </form>

      <ExamSelectorDialog
        isOpen={isExamSelectorVisible}
        onClose={() => setIsExamSelectorVisible(false)}
        handleConfirm={(examIds: number[]) => setValue('examIds', examIds)}
        excludedExams={[]}
      />

      {email && (
        <Dialog
          open={!!emailOpen}
          onClose={() => isEmailOpen(false)}
          TransitionComponent={UpTransition}
          slotProps={{ backdrop: {} }}
        >
          <DialogContent>
            {!!emailOpen && (
              <MssEmailForm onClose={() => isEmailOpen(false)} email={email} />
            )}
          </DialogContent>
        </Dialog>
      )}

      <Dialog
        open={!!doctorFormOpen}
        onClose={() => setDoctorFormOpen(false)}
        TransitionComponent={UpTransition}
        slotProps={{ backdrop: {} }}
      >
        <DialogContent>
          {!!doctorFormOpen && (
            <DoctorForm onClose={() => setDoctorFormOpen(false)} />
          )}
        </DialogContent>
      </Dialog>

      <Dialog
        open={!!patientFormOpen}
        onClose={() => setPatientFormOpen(false)}
        TransitionComponent={UpTransition}
        slotProps={{ backdrop: {} }}
        maxWidth={false}
        className="flex justify-center items-center"
      >
        <DialogContent className="w-full sm:w-[95%] max-w-[1000px]">
          <div className="flex items-center space-x-4 justify-end pr-4">
            <button
              type="button"
              className="px-4 py-2 bg-indigo-700 hover:bg-indigo-800 text-white rounded-lg flex items-center disabled:opacity-50"
              disabled={isSubmittingPatient}
              onClick={handleSavePatient}
            >
              {isSubmittingPatient && <Spinner className="mr-2" size={18} />}
              Sauvegarder
            </button>

            <button
              type="button"
              className="px-4 py-2 bg-gray-400 hover:bg-gray-500 text-white rounded-lg flex items-center disabled:opacity-50"
              disabled={isSubmittingPatient}
              onClick={() => setPatientFormOpen(false)}
            >
              Fermer
            </button>
          </div>
          <ManagePatientComponent />
        </DialogContent>
      </Dialog>
    </div>
  )
}

export default MssRequestForm
